import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { addDays, isBetweenDates, isSameDate, toDate } from "../../utils/timelineUtils";
import { OssStatusChip } from "../OssStatusChip";

const dayStart = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const startOfWeek = (date) => {
  const start = dayStart(date);
  start.setDate(start.getDate() - start.getDay());
  return start;
};

const endOfWeek = (date) => addDays(startOfWeek(date), 6);

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);

const formatKoreanDate = (date) =>
  date ? `${date.getMonth() + 1}/${date.getDate()}` : "-";

const isActionStatus = (row) =>
  ["CHECK_REQUIRED", "CHANGE_REVIEW", "PLAN_REVIEW"].includes(row.schedule_status);

const isRiskAction = (row) => ["OVERDUE", "HIGH"].includes(row.risk_level);

const isOpenSchedule = (row) => !["COMPLETED", "CANCELLED"].includes(row.schedule_status);

const getCheckDate = (row) => toDate(row.check_required_date || row.required_start_date);

const getTodayWorkRows = (rows, today) =>
  rows
    .filter(isOpenSchedule)
    .filter((row) => {
      const checkDate = getCheckDate(row);
      return (
        (checkDate && checkDate <= today) ||
        isBetweenDates(today, row.startDate, row.dueDate) ||
        isActionStatus(row) ||
        isRiskAction(row)
      );
    })
    .sort((a, b) => {
      const leftCheck = getCheckDate(a)?.getTime?.() || a.startDate?.getTime?.() || 0;
      const rightCheck = getCheckDate(b)?.getTime?.() || b.startDate?.getTime?.() || 0;
      return leftCheck - rightCheck || String(a.orderNo).localeCompare(String(b.orderNo), "ko");
    });

const getRowsInRange = (rows, start, end, mode) =>
  rows
    .filter(isOpenSchedule)
    .filter((row) => {
      if (mode === "due") return isBetweenDates(row.dueDate, start, end);
      return row.startDate <= end && row.dueDate >= start;
    })
    .sort((a, b) => a.startDate - b.startDate || a.dueDate - b.dueDate || String(a.orderNo).localeCompare(String(b.orderNo), "ko"));

const FocusMetric = ({ icon, label, value, helper, tone = "#6c5ce7" }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.55,
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      bgcolor: "#fff",
      minWidth: 0,
    }}
  >
    <Stack direction="row" spacing={1.1} alignItems="center">
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          color: tone,
          bgcolor: `${tone}12`,
          border: `1px solid ${tone}28`,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "#475569" }}>{label}</Typography>
        <Typography sx={{ mt: 0.1, fontSize: 24, fontWeight: 900, color: "#111827", lineHeight: 1.1 }}>
          {value}
          <Box component="span" sx={{ ml: 0.25, fontSize: 12, fontWeight: 700, color: "#64748b" }}>
            건
          </Box>
        </Typography>
        <Typography noWrap sx={{ mt: 0.25, fontSize: 11.5, color: "#64748b" }}>
          {helper}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const WorkRow = ({ row, onOpenOrder }) => (
  <Paper
    component="button"
    type="button"
    elevation={0}
    onClick={() => onOpenOrder?.(row.orderId)}
    sx={{
      width: "100%",
      p: 1.25,
      border: "1px solid #e5e7eb",
      borderLeft: `4px solid ${row.riskTone}`,
      borderRadius: "8px",
      bgcolor: "#fff",
      textAlign: "left",
      cursor: "pointer",
      transition: "border-color 140ms ease, background-color 140ms ease",
      "&:hover": {
        borderColor: "#cbd5e1",
        bgcolor: "#fbfcff",
      },
    }}
  >
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.2}>
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 900, color: "#111827" }}>
          {row.orderNo} · {row.itemName}
        </Typography>
        <Typography noWrap sx={{ mt: 0.25, fontSize: 12.2, color: "#64748b" }}>
          {row.customerName} / 착수 {row.startLabel} / 납기 {row.dueLabel}
        </Typography>
      </Box>
      <Stack direction="row" spacing={0.45} alignItems="center" sx={{ flexShrink: 0 }}>
        <OssStatusChip value={row.schedule_status} />
        <OssStatusChip type="risk" value={row.risk_level} />
      </Stack>
    </Stack>
  </Paper>
);

const WorkList = ({ title, helper, rows, emptyText, onOpenOrder }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.55,
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      bgcolor: "#fff",
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
    }}
  >
    <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 900, color: "#111827" }}>{title}</Typography>
        <Typography sx={{ mt: 0.2, fontSize: 12, color: "#64748b" }}>{helper}</Typography>
      </Box>
      <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "#64748b" }}>{rows.length}건</Typography>
    </Stack>
    <Stack spacing={0.8} sx={{ minHeight: 0, overflow: "hidden" }}>
      {rows.length ? (
        rows.slice(0, 5).map((row) => <WorkRow key={`${title}-${row.id}`} row={row} onOpenOrder={onOpenOrder} />)
      ) : (
        <Box sx={{ p: 2, borderRadius: "8px", bgcolor: "#f8fafc", color: "#64748b", fontSize: 13, textAlign: "center" }}>
          {emptyText}
        </Box>
      )}
    </Stack>
  </Paper>
);

export const OssDashboardWorkFocus = ({ rows = [], onOpenOrder }) => {
  const today = useMemo(() => dayStart(new Date()), []);
  const weekStart = useMemo(() => startOfWeek(today), [today]);
  const weekEnd = useMemo(() => endOfWeek(today), [today]);
  const monthStart = useMemo(() => startOfMonth(today), [today]);
  const monthEnd = useMemo(() => endOfMonth(today), [today]);

  const todayRows = useMemo(() => getTodayWorkRows(rows, today), [rows, today]);
  const weekRows = useMemo(() => getRowsInRange(rows, weekStart, weekEnd, "overlap"), [rows, weekStart, weekEnd]);
  const monthDueRows = useMemo(() => getRowsInRange(rows, monthStart, monthEnd, "due"), [rows, monthStart, monthEnd]);
  const dueToday = useMemo(() => rows.filter((row) => isSameDate(row.dueDate, today)), [rows, today]);

  return (
    <Stack spacing={1.25} sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
          gap: 1,
          flexShrink: 0,
        }}
      >
        <FocusMetric
          icon={<TodayOutlinedIcon fontSize="small" />}
          label="오늘 확인"
          value={todayRows.length}
          helper={`${formatKoreanDate(today)} 기준 확인/통화 대상`}
          tone="#6c5ce7"
        />
        <FocusMetric
          icon={<TaskAltOutlinedIcon fontSize="small" />}
          label="이번주 일정"
          value={weekRows.length}
          helper={`${formatKoreanDate(weekStart)}~${formatKoreanDate(weekEnd)} 착수-납기`}
          tone="#2563eb"
        />
        <FocusMetric
          icon={<CalendarMonthOutlinedIcon fontSize="small" />}
          label="이번달 납기"
          value={monthDueRows.length}
          helper={`${today.getMonth() + 1}월 요청 납기`}
          tone="#14b8a6"
        />
        <FocusMetric
          icon={<ErrorOutlineRoundedIcon fontSize="small" />}
          label="오늘 납기"
          value={dueToday.length}
          helper="요청 납기가 오늘인 품목"
          tone="#ef4444"
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", xl: "1.1fr 0.95fr 0.95fr" },
          gap: 1,
          overflow: "hidden",
        }}
      >
        <WorkList
          title="오늘 할 일"
          helper="확인일이 지났거나 위험도가 높은 주문"
          rows={todayRows}
          emptyText="오늘 바로 확인할 주문이 없습니다."
          onOpenOrder={onOpenOrder}
        />
        <WorkList
          title="이번주 생산/착수"
          helper="이번주 기간에 걸쳐 있는 착수-납기 일정"
          rows={weekRows}
          emptyText="이번주에 표시할 일정이 없습니다."
          onOpenOrder={onOpenOrder}
        />
        <WorkList
          title="이번달 납기"
          helper="이번달 요청 납기가 도래하는 품목"
          rows={monthDueRows}
          emptyText="이번달 납기 품목이 없습니다."
          onOpenOrder={onOpenOrder}
        />
      </Box>
    </Stack>
  );
};
